import React, { useState, lazy, Suspense, useEffect } from "react";
import "./../managerPage/css/assetTap.css";
import AssetTap from "./../managerPage/AssetTap";
import { useGetCategoryQuery } from "../../slices/assetApiSlice";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const categoryMapping = {
  Building: "Building",
  Infrastructure: "Other",
  Facility: "Other",
  Landscaping: "Landscaping",
  Machinery: "Machinery",
  Furniture: "Other",
  RoomQR: "RoomQR",
};

// Default component when category is not found
const DefaultCategory = lazy(() => import("./tab/Other"));

// Static RoomQr component
const RoomQrComponent = lazy(() => import("./tab/Room"));

const AdminAssets = () => {
  const { data: categories, error, isLoading } = useGetCategoryQuery();

  // Use Sonner for loading state
  useEffect(() => {
    if (isLoading) {
      toast.loading("Loading data, please wait...");
    } else {
      toast.dismiss();
    }
    return () => toast.dismiss();
  }, [isLoading]);

  useEffect(() => {
    if (error) {
      toast.error("Error Fetching Categories", {
        description: "Unable to load asset categories.",
      });
    }
  }, [error]);

  // Filter out categories where 'deleted' is true
  const activeCategories = categories
    ? categories.filter((category) => !category.deleted)
    : [];

  // Ensure categories exist before setting defaultTab
  const defaultTab =
    activeCategories.length > 0 ? activeCategories[0].name : "Building";
  const [activeTab, setActiveTab] = useState(defaultTab);

  // Adding RoomQr manually to categories to display in the AssetTap
  const categoryList = [
    ...activeCategories.slice(0, 1),
    { name: "Room QR" },
    ...activeCategories.slice(1),
  ];

  // Get the correct filename based on the category name
  const normalizedTab = activeTab.replace(/\s+/g, "").toLowerCase();
  const componentFile = categoryMapping[activeTab] || "Other";

  // Lazy load the mapped component dynamically or load RoomQr if selected
  const CategoryComponent =
    normalizedTab === "roomqr"
      ? RoomQrComponent
      : lazy(() =>
          /* @vite-ignore */
          import(`./tab/${componentFile}.jsx`).catch(() => DefaultCategory),
        );

  // Add loading state for Suspense fallback
  const [tabLoading, setTabLoading] = useState(false);

  // Handle tab change with loading indicator
  const handleTabChange = (tab) => {
    setTabLoading(true);
    setActiveTab(tab);
    // The loading state will be handled by Suspense
  };

  return (
    <div className="flex flex-col h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-card">
        <div className="px-6 py-4">
          <h1 className="text-2xl font-bold tracking-tight">
            Assets Management
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage and organize assets across different categories
          </p>
        </div>

        {/* Tabs */}
        <div className="px-6">
          <div className="flex flex-wrap gap-1 border-b border-border">
            {categoryList.map((category) => (
              <button
                key={category.name}
                onClick={() => handleTabChange(category.name)}
                className={`relative px-4 py-2.5 text-sm font-medium transition-all rounded-t-lg ${
                  activeTab === category.name
                    ? "bg-background text-foreground border-b-2 border-primary"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                }`}
              >
                {category.name}
                {activeTab === category.name && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-auto">
        <div className="p-6">
          <Suspense
            fallback={
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
                <p className="text-muted-foreground">Loading content...</p>
              </div>
            }
          >
            <CategoryComponent
              category={activeTab}
              onLoad={() => setTabLoading(false)}
            />
          </Suspense>
        </div>
      </div>
    </div>
  );
};

export default AdminAssets;
